// src/hooks/useAddBrand.ts
import { useState, useCallback } from "react";
import { useNavigation } from "./useNavigation";
import {
  ConnectedPlatforms,
  FormValues,
  FormData,
  ValidationRules,
  UseAddBrandProps,
  UseAddBrandReturn,
} from "../types/add-brand-types";
import useBrandApi from "../api/use-add-brand-api";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../store/store";
import { fetchBrandList } from "../store/features/dashboard/dashboardSlice";
import useStrategyApi from "../api/use-strategy-api";
import { buildWebsiteContextSourceName } from "../utils/website-context";
import { trackEvent } from "../utils/firebase-analytics";

export const useAddBrand = ({
  onSuccess,
  onCancel,
}: UseAddBrandProps = {}): UseAddBrandReturn => {
  const { goBack } = useNavigation();
  const { addBrand } = useBrandApi();
  const { triggerWebsiteContextIngestion } = useStrategyApi();
  const dispatch = useDispatch<AppDispatch>();
  const [connectedPlatforms, setConnectedPlatforms] =
    useState<ConnectedPlatforms>({
      linkedin: false,
      twitter: false,
      blog: false,
    });

  // Initial form values
  const initialValues: FormValues = {
    brand_name: "",
    brand_url: "",
    brand_tone: "",
    target_audience: "",
    banned_words: [],
    competitors: [],
  };

  // Validation rules
  const validationRules: ValidationRules = {
    brand_name: {
      required: true,
      requiredMessage: "Brand name is required",
    },
    brand_url: {
      required: true,
      requiredMessage: "Brand URL is required",
    },
    brand_tone: {
      required: false,
      requiredMessage: "Brand tone is required",
    },
    target_audience: {
      required: false,
      requiredMessage: "Target audience is required",
    },
  };

  // Handle form submission
  const handleSubmit = useCallback(
    async (values: FormValues): Promise<void> => {
      const formData: FormData = {
        ...values,
        competitors: values.competitors?.filter((c) => c.trim() !== "") ?? [],
      };

      const response = await addBrand(formData);
      if (response?.status === "success") {
        const createdBrandId = response.data?.id;
        const createdBrandUrl =
          response.data?.marketing_strategy?.brand_url || formData.brand_url;

        if (createdBrandId && createdBrandUrl?.trim()) {
          trackEvent("website_context_ingestion_started", {
            source: "brand_create",
          });
          void triggerWebsiteContextIngestion(
            createdBrandId,
            createdBrandUrl,
            buildWebsiteContextSourceName(createdBrandUrl)
          );
        }

        trackEvent("brand_created", {
          has_url: Boolean(createdBrandUrl?.trim()),
          has_tone: Boolean(formData.brand_tone?.trim()),
          has_target_audience: Boolean(formData.target_audience?.trim()),
          competitor_count: formData.competitors.length,
          banned_word_count: formData.banned_words?.length ?? 0,
        });
        await dispatch(fetchBrandList());
        if (onSuccess) await onSuccess();
        else goBack();
        return;
      }

      console.error(response?.message || "Failed to create brand");
    },
    [addBrand, dispatch, goBack, onSuccess, triggerWebsiteContextIngestion]
  );

  // Handle cancel button
  const handleCancel = useCallback((): void => {
    if (onCancel) onCancel();
    else goBack();
  }, [onCancel, goBack]);

  // Toggle platform connection
  const handlePlatformToggle = useCallback(
    (platform: keyof ConnectedPlatforms): void => {
      setConnectedPlatforms((prev) => ({
        ...prev,
        [platform]: !prev[platform],
      }));
    },
    []
  );

  return {
    connectedPlatforms,
    initialValues,
    validationRules,
    handleSubmit,
    handleCancel,
    handlePlatformToggle,
  };
};
